import { Middleware, size } from '@floating-ui/core';
import { autoUpdate, autoPlacement, computePosition, ComputePositionConfig, ComputePositionReturn, offset, shift } from '@floating-ui/dom';
import { ObjectDirective } from 'vue';

export type StickDirective<TFloatingElement extends HTMLElement, TReferenceElement extends Element> = ObjectDirective<TFloatingElement, TReferenceElement>;

export function createStickDirective<TFloatingElement extends HTMLElement, TReferenceElement extends Element>(): StickDirective<
  TFloatingElement,
  TReferenceElement
> {
  type BeforeMountDirectiveHook = ThisDirective['beforeMount'];
  type MountedDirectiveHook = ThisDirective['mounted'];
  type BeforeUnmountDirectiveHook = ThisDirective['beforeUnmount'];
  type FloatingElementAutoPositioningDisposer = ReturnType<typeof autoUpdate>;
  type ThisDirective = StickDirective<TFloatingElement, TReferenceElement>;

  const DistanceFromControlToHelper = 3;
  const DistanceFromElementToWindowBorder = 30;
  const FloatingElementStackIndex = 3000;

  let dispose!: FloatingElementAutoPositioningDisposer;

  const beforeMount: BeforeMountDirectiveHook = (floating, { value: reference }) => {
    dispose = useFloatingElementAutoPositioning(floating, reference);
  };

  const mounted: MountedDirectiveHook = (floating, { value: reference }) => {
    refreshFloatingElementStyle(floating, reference);
  };

  const beforeUnmount: BeforeUnmountDirectiveHook = () => dispose();

  function useFloatingElementAutoPositioning(floating: TFloatingElement, reference: TReferenceElement): FloatingElementAutoPositioningDisposer {
    return autoUpdate(reference, floating, () => refreshFloatingElementStyle(floating, reference));
  }

  async function refreshFloatingElementStyle(floating: TFloatingElement, reference: TReferenceElement): Promise<void> {
    Object.assign(floating.style, await computeFloatingElementStyle(floating, reference));
  }

  async function computeFloatingElementStyle(floating: TFloatingElement, reference: TReferenceElement): Promise<Partial<CSSStyleDeclaration>> {
    const { position, sizeData } = await computeFloatingElementPosition(floating, reference);
    const maxHeight = Math.min(sizeData.availableHeight - DistanceFromElementToWindowBorder, 480);
    return {
      left: `${position.x - (position?.middlewareData?.shift?.x || 0)}px`,
      top: `${position.y}px`,
      position: 'absolute',
      maxHeight: `${maxHeight}px`,
      zIndex: String(FloatingElementStackIndex)
    };
  }

  async function computeFloatingElementPosition(floating: TFloatingElement, reference: TReferenceElement) {
    const sizeData = { availableHeight: 60, availableWidth: 60 };
    const middleware: Middleware[] = [
      autoPlacement({ allowedPlacements: ['top', 'bottom'] }),
      offset(DistanceFromControlToHelper),
      shift(),
      size({
        apply: (args) => {
          sizeData.availableHeight = args.availableHeight;
          sizeData.availableWidth = args.availableWidth;
        }
      })
    ];
    const config: Partial<ComputePositionConfig> = { middleware };
    const position = await computePosition(reference, floating, config);
    return { position, sizeData };
  }

  return { beforeMount, beforeUnmount, mounted };
}
